import React, { useState, useRef, useEffect } from "react";
import { RichLink, YouTubeIcon } from "./RichLink";
import { cn } from "@/lib/utils";
import { isSafeHttpUrl } from "@/lib/urlSafety";
import { getScoreAppLinkLabel } from "@/lib/richText";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

/** Matches [text](url), [SHORTCODE] (not followed by '('), or plain https URLs. */
const TOKEN_REGEX = /\[([^\]]+)\]\(([^)]+)\)|\[([A-Za-z0-9_!?-]+)\](?!\()|(https?:\/\/[^\s]+)/g;

export type LinkPart = { type: "mdlink"; text: string; url: string; start: number; end: number };
export type ShortcodePart = { type: "shortcode"; code: string };
export type PlainPart = { type: "plain"; text: string };
export type TextPart = LinkPart | ShortcodePart | PlainPart;

const SHORTCODE_STYLES: Record<string, string> = {
  "NEW": "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  "!": "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30",
  "IMPORTANT": "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30",
  "URGENT": "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30",
  "TODO": "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30",
  "WIP": "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30",
  "DONE": "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  "OK": "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  "FIXED": "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  "HOT": "bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/30",
  "REVIEW": "bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/30",

  // Progress level scale [1] - [10] (Red -> Orange -> Yellow -> Lime -> Green)
  "1": "bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/40 font-mono",
  "2": "bg-red-500/15 text-red-500 dark:text-red-400 border-red-500/30 font-mono",
  "3": "bg-orange-600/15 text-orange-600 dark:text-orange-400 border-orange-600/30 font-mono",
  "4": "bg-orange-500/15 text-orange-500 dark:text-orange-400 border-orange-500/30 font-mono",
  "5": "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 font-mono",
  "6": "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/30 font-mono",
  "7": "bg-lime-500/15 text-lime-600 dark:text-lime-400 border-lime-500/30 font-mono",
  "8": "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 font-mono",
  "9": "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30 font-mono",
  "10": "bg-emerald-600/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/40 font-mono font-extrabold",
};

const DEFAULT_SHORTCODE_STYLE = "bg-secondary text-secondary-foreground border-border";

function ShortcodePill({ code }: { code: string }) {
  const upperCode = code.toUpperCase();
  const style = SHORTCODE_STYLES[upperCode] || DEFAULT_SHORTCODE_STYLE;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold border rounded-md tracking-wider align-baseline select-none shrink-0 mr-1.5 my-0.5 ${style}`}
    >
      {code}
    </span>
  );
}

/** Renders a segment that may contain **bold** and *italic* */
function renderFormatted(segment: string, keyPrefix: string) {
  const tokens: Array<{ t: "b" | "i" | "p"; s: string }> = [];
  let last = 0;
  let m;
  const combined = /\*\*([\s\S]+?)\*\*|\*([^\n*]+?)\*/g;
  while ((m = combined.exec(segment)) !== null) {
    if (m.index > last) tokens.push({ t: "p", s: segment.slice(last, m.index) });
    if (m[1] != null) tokens.push({ t: "b", s: m[1] });
    else if (m[2] != null) tokens.push({ t: "i", s: m[2] });
    last = combined.lastIndex;
  }
  if (last < segment.length) tokens.push({ t: "p", s: segment.slice(last) });
  const elements = tokens.map((tok, i) => {
    if (tok.t === "b") return <strong key={`${keyPrefix}-${i}`} className="font-semibold text-foreground">{tok.s}</strong>;
    if (tok.t === "i") return <em key={`${keyPrefix}-${i}`}>{tok.s}</em>;
    return <span key={`${keyPrefix}-${i}`}>{tok.s}</span>;
  });
  return elements.length === 1 && tokens[0].t === "p" ? elements[0] : <>{elements}</>;
}

interface TextWithLinksProps {
  text: string;
  /** When provided, links show edit chip on hover (view mode) */
  onEditLink?: (linkText: string, linkUrl: string, start: number, end: number, anchor: HTMLElement | null) => void;
  onUpdateLink?: (start: number, end: number, newUrl: string) => void;
  onRemoveLink?: (start: number, end: number) => void;
  /** Called when user clicks a to-do checkbox on a line starting at `lineOffset` */
  onToggleTodo?: (lineOffset: number, nextChecked: boolean) => void;
  /** Progress report: fetch link previews aggressively (avoids stale SW / React Query edge cases). */
  richLinkVariant?: "default" | "report";
  /** Control link visual style: default (cards/pills for standalone URLs) or inline (underlined text with external icon). */
  linkVariant?: "default" | "inline";
  /** Internal character offset when recursing into lines or list items */
  baseOffset?: number;
}


/**
 * Renders text with:
 * - Markdown links [text](url) as clickable links
 * - Shortcode pills [NEW], [!], [TODO], etc.
 * - Plain URLs (http/https) as RichLink cards (or inline links if linkVariant="inline")
 * - **bold** and *italic*
 * - Optional: edit chip on link hover when onEditLink provided
 */
export function TextWithLinks({
  text,
  onEditLink,
  onRemoveLink,
  onUpdateLink,
  onToggleTodo,
  richLinkVariant = "default",
  linkVariant = "default",
  baseOffset = 0,
}: TextWithLinksProps) {
  const normalizedText = (text ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  if (normalizedText) {
    const isMultiline = normalizedText.includes("\n");

    if (isMultiline) {
      const lines = normalizedText.split("\n");
      let runningOffset = baseOffset;
      return (
        <span className="block space-y-0.5">
          {lines.map((line, lineIndex) => {
            const lineStart = runningOffset;
            runningOffset += line.length + 1;
            if (line.trim().length === 0) {
              return <span key={lineIndex} className="block h-2" />;
            }
            return (
              <span key={lineIndex} className="block">
                <TextWithLinks
                  text={line}
                  onEditLink={onEditLink}
                  onRemoveLink={onRemoveLink}
                  onUpdateLink={onUpdateLink}
                  onToggleTodo={onToggleTodo}
                  richLinkVariant={richLinkVariant}
                  linkVariant={linkVariant}
                  baseOffset={lineStart}
                />
              </span>
            );
          })}
        </span>
      );
    }

    // Support to-do items: [ ] task, [x] task, [] task, - [ ] task, wrapped in formatting or link
    const todoMatch = normalizedText.match(/^(\s*)(?:[-*•–—]\s+)?\[([ xX]?)\](?:\s+(.*)|$)/);
    const todoDelimMatch = normalizedText.match(/^(\s*)(\*{1,2})(?:[-*•–—]\s+)?\[([ xX]?)\]\s+([\s\S]*)$/);
    const todoLinkMatch = normalizedText.match(/^(\s*)\[(?:[-*•–—]\s+)?\[([ xX]?)\]\s+([^\]]+)\]\(([^)]+)\)(.*)$/);

    if (todoMatch || todoDelimMatch || todoLinkMatch) {
      const checkChar = todoMatch
        ? todoMatch[2]
        : todoDelimMatch
          ? todoDelimMatch[3]
          : todoLinkMatch![2];
      const isChecked = checkChar.toLowerCase() === "x";
      let content = todoMatch ? (todoMatch[3] ?? "") : "";
      let childOffset = baseOffset;
      if (todoMatch) {
        const rest = todoMatch[3] ?? "";
        childOffset = baseOffset + (normalizedText.length - rest.length);
      } else if (todoDelimMatch) {
        const delim = todoDelimMatch[2];
        let inner = todoDelimMatch[4];
        if (!inner.endsWith(delim)) {
          inner = inner + delim;
        }
        content = delim + inner;
      } else if (todoLinkMatch) {
        content = `[${todoLinkMatch[3]}](${todoLinkMatch[4]})${todoLinkMatch[5]}`;
      }

      return (
        <span className="flex items-start gap-2 pl-0.5 my-0.5">
          <button
            type="button"
            role="checkbox"
            aria-checked={isChecked}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleTodo?.(baseOffset, !isChecked);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className={cn(
              "relative after:absolute after:-inset-2.5 mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors select-none",
              onToggleTodo ? "cursor-pointer" : "cursor-default",
              isChecked
                ? "border-primary bg-primary text-primary-foreground"
                : "border-primary/50 bg-background hover:bg-muted"
            )}
          >
            {isChecked && (
              <span className="material-icons text-[13px] font-bold leading-none select-none">check</span>
            )}
          </button>
          <span className={cn("min-w-0 flex-1 leading-[1.25rem]", isChecked && "text-muted-foreground")}>
            <TextWithLinks
              text={content}
              onEditLink={onEditLink}
              onRemoveLink={onRemoveLink}
              onUpdateLink={onUpdateLink}
              onToggleTodo={onToggleTodo}
              richLinkVariant={richLinkVariant}
              linkVariant={linkVariant}
              baseOffset={childOffset}
            />
          </span>
        </span>
      );
    }

    // List item parsing for single line:
    // Support bullets: standard (-, *, •, –, —), wrapped in formatting (**- , *-, etc.), or wrapped in a markdown link ([- text](url))
    const bulletMatch = normalizedText.match(/^(\s*)([-*•–—])\s+(.*)$/);
    const bulletDelimMatch = normalizedText.match(/^(\s*)(\*{1,2})([–—\-*•])\s+([\s\S]*)$/);
    const bulletLinkMatch = normalizedText.match(/^(\s*)\[[–—\-*•]\s+([^\]]+)\]\(([^)]+)\)(.*)$/);

    if (bulletMatch || bulletDelimMatch || bulletLinkMatch) {
      let content = bulletMatch ? bulletMatch[3] : "";
      let childOffset = baseOffset;
      if (bulletMatch) {
        childOffset = baseOffset + (normalizedText.length - bulletMatch[3].length);
      } else if (bulletDelimMatch) {
        const delim = bulletDelimMatch[2];
        let inner = bulletDelimMatch[4];
        if (!inner.endsWith(delim)) {
          inner = inner + delim;
        }
        content = delim + inner;
      } else if (bulletLinkMatch) {
        content = `[${bulletLinkMatch[2]}](${bulletLinkMatch[3]})${bulletLinkMatch[4]}`;
      }

      return (
        <span className="flex items-start gap-2 pl-0.5 my-0.5">
          <span className="select-none text-primary/80 shrink-0 text-sm leading-snug">•</span>
          <span className="min-w-0 flex-1">
            <TextWithLinks
              text={content}
              onEditLink={onEditLink}
              onRemoveLink={onRemoveLink}
              onUpdateLink={onUpdateLink}
              richLinkVariant={richLinkVariant}
              linkVariant={linkVariant}
              baseOffset={childOffset}
            />
          </span>
        </span>
      );
    }

    // Support numbers: standard (1., 1)), wrapped in formatting (**1., *1), etc.), or wrapped in a markdown link ([1. text](url))
    const numberMatch = normalizedText.match(/^(\s*)(\d+)[.)]\s+(.*)$/);
    const numberDelimMatch = normalizedText.match(/^(\s*)(\*{1,2})(\d+)[.)]\s+([\s\S]*)$/);
    const numberLinkMatch = normalizedText.match(/^(\s*)\[(\d+)[.)]\s+([^\]]+)\]\(([^)]+)\)(.*)$/);

    if (numberMatch || numberDelimMatch || numberLinkMatch) {
      const num = numberMatch
        ? numberMatch[2]
        : numberDelimMatch
          ? numberDelimMatch[3]
          : numberLinkMatch![2];
      let content = numberMatch ? numberMatch[3] : "";
      let childOffset = baseOffset;
      if (numberMatch) {
        childOffset = baseOffset + (normalizedText.length - numberMatch[3].length);
      } else if (numberDelimMatch) {
        const delim = numberDelimMatch[2];
        let inner = numberDelimMatch[4];
        if (!inner.endsWith(delim)) {
          inner = inner + delim;
        }
        content = delim + inner;
      } else if (numberLinkMatch) {
        content = `[${numberLinkMatch[3]}](${numberLinkMatch[4]})${numberLinkMatch[5]}`;
      }

      return (
        <span className="flex items-start gap-1.5 pl-0.5 my-0.5">
          <span className="select-none text-muted-foreground/80 tabular-nums shrink-0 font-medium text-xs pt-0.5 w-4 text-right">
            {num}.
          </span>
          <span className="min-w-0 flex-1">
            <TextWithLinks
              text={content}
              onEditLink={onEditLink}
              onRemoveLink={onRemoveLink}
              onUpdateLink={onUpdateLink}
              richLinkVariant={richLinkVariant}
              linkVariant={linkVariant}
              baseOffset={childOffset}
            />
          </span>
        </span>
      );
    }
  }

  const parts: Array<TextPart> = [];
  let lastIndex = 0;
  let match;
  const re = new RegExp(TOKEN_REGEX.source, "g");
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "plain", text: text.slice(lastIndex, match.index) });
    }
    if (match[1] != null && match[2] != null) {
      // Reject URLs that aren't http(s)/mailto so `[click](javascript:...)` etc.
      // downgrade to plain text instead of rendering as a live anchor.
      if (isSafeHttpUrl(match[2])) {
        parts.push({ type: "mdlink", text: match[1], url: match[2], start: baseOffset + match.index, end: baseOffset + re.lastIndex });
      } else {
        parts.push({ type: "plain", text: match[1] });
      }
    } else if (match[3] != null) {
      parts.push({ type: "shortcode", code: match[3] });
    } else if (match[4] != null) {
      // The bare-URL branch already requires http(s):// via the regex, but we
      // double-check with the shared sanitizer to keep the invariant in one place.
      if (isSafeHttpUrl(match[4])) {
        parts.push({ type: "mdlink", text: match[4], url: match[4], start: baseOffset + match.index, end: baseOffset + re.lastIndex });
      } else {
        parts.push({ type: "plain", text: match[4] });
      }
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push({ type: "plain", text: text.slice(lastIndex) });
  }

  // Only show a plain URL as a RichLink card when it is the sole content in the block.
  // When mixed with surrounding text the card style breaks inline text flow.
  const isUrlOnlyBlock = parts.length === 1 && parts[0].type === "mdlink";

  return (
    <>
      {parts.map((part, i) => {
        if (part.type === "mdlink") {
          return (
            <LinkWithPreview
              key={i}
              part={part}
              onEditLink={onEditLink}
              onUpdateLink={onUpdateLink}
              onRemoveLink={onRemoveLink}
              rich={part.text === part.url && isUrlOnlyBlock}
              eagerRichPreview={richLinkVariant === "report"}
              linkVariant={linkVariant}
            />
          );
        }
        if (part.type === "shortcode") {
          return <ShortcodePill key={i} code={part.code} />;
        }

        let plainText = part.text;
        if (i > 0 && parts[i - 1].type === "shortcode" && plainText.startsWith(" ")) {
          plainText = plainText.slice(1);
        }

        if (!plainText) return null;

        return <React.Fragment key={i}>{renderFormatted(plainText, `p-${i}`)}</React.Fragment>;
      })}
    </>
  );
}

function LinkWithPreview({
  part,
  onEditLink,
  onUpdateLink,
  onRemoveLink,
  rich,
  eagerRichPreview,
  linkVariant = "default",
}: {
  part: LinkPart;
  onEditLink?: (linkText: string, linkUrl: string, start: number, end: number, anchor: HTMLElement | null) => void;
  onUpdateLink?: (start: number, end: number, newUrl: string) => void;
  onRemoveLink?: (start: number, end: number) => void;
  rich?: boolean;
  eagerRichPreview?: boolean;
  linkVariant?: "default" | "inline";
}) {
  const [copied, setCopied] = useState(false);
  const [editingUrl, setEditingUrl] = useState(false);
  const [tempUrl, setTempUrl] = useState(part.url);
  const linkRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (!editingUrl) {
      isSavingRef.current = false;
    }
  }, [editingUrl]);

  const handleUrlSave = () => {
    if (tempUrl !== part.url) {
      onUpdateLink?.(part.start, part.end, tempUrl);
    }
    setEditingUrl(false);
  };


  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(part.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingUrl(true);
    setTempUrl(part.url);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoveLink?.(part.start, part.end);
  };

  const isYouTube = part.url.includes("youtube.com") || part.url.includes("youtu.be");

  const isAnchor = part.url.startsWith("#");
  const displayText =
    part.text === part.url ? getScoreAppLinkLabel(part.url) ?? part.text : part.text;

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span ref={linkRef} className="cursor-pointer">
          {linkVariant === "inline" ? (
            <a
              href={part.url}
              target={isAnchor ? undefined : "_blank"}
              rel={isAnchor ? undefined : "noopener noreferrer"}
              className="inline-flex items-baseline gap-1 text-primary hover:text-primary/80 font-medium break-all no-underline"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span className="underline underline-offset-2">{renderFormatted(displayText, `link-${part.start}`)}</span>
              {!isAnchor && <span className="material-icons text-[13px] no-underline opacity-80 shrink-0 select-none translate-y-0.5">open_in_new</span>}
            </a>
          ) : isYouTube ? (
            part.text === part.url ? (
              <RichLink url={part.url} eagerPreview={eagerRichPreview} />
            ) : (
              <a
                href={part.url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "inline-flex items-center gap-2 px-3 py-1 rounded-xl shrink-0",
                  "bg-slate-900/80 dark:bg-slate-900/90 border border-black/10 dark:border-white/10",
                  "hover:bg-slate-800/90 hover:border-white/20",
                  "transition-all no-underline text-foreground align-middle my-0.5",
                  "-ml-0.5 mr-1"
                )}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <YouTubeIcon className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium leading-tight whitespace-nowrap text-foreground">
                  {renderFormatted(displayText, `link-${part.start}`)}
                </span>
              </a>
            )
          ) : rich ? (
            <RichLink url={part.url} eagerPreview={eagerRichPreview} />
          ) : (
            <a
              href={part.url}
              target={isAnchor ? undefined : "_blank"}
              rel={isAnchor ? undefined : "noopener noreferrer"}
              className="text-primary underline hover:text-primary/80"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {renderFormatted(displayText, `link-${part.start}`)}
            </a>
          )}
        </span>
      </HoverCardTrigger>
      {onEditLink && (
        <HoverCardContent
          align="start"
          side="bottom"
          sideOffset={5}
          className="w-auto p-1 flex flex-col gap-1 bg-popover/95 backdrop-blur-sm border-border/50 shadow-lg"
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1">
            <div className="flex items-center gap-2 px-2 py-1 max-w-[240px]">
              <span className="material-icons text-xs text-muted-foreground shrink-0">public</span>
              {editingUrl ? (
                <input
                  ref={inputRef}
                  value={tempUrl}
                  onChange={(e) => setTempUrl(e.target.value)}
                  onBlur={() => {
                    if (!isSavingRef.current) {
                      setEditingUrl(false);
                      setTempUrl(part.url);
                    }
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter") {
                      e.preventDefault();
                      isSavingRef.current = true;
                      handleUrlSave();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setEditingUrl(false);
                      setTempUrl(part.url);
                    }
                  }}
                  className="h-5 px-1 text-xs bg-background border rounded w-[180px] focus:outline-none focus:ring-1 focus:ring-ring"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span
                  className="text-xs text-popover-foreground hover:underline truncate cursor-text block max-w-[180px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingUrl(true);
                    setTempUrl(part.url);
                  }}
                  title="Click to edit URL"
                >
                  {part.url}
                </span>
              )}
            </div>
            <div className="flex items-center gap-0.5 border-l border-border/50 pl-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 rounded-sm text-muted-foreground hover:text-foreground"
                onClick={handleCopy}
                title="Copy URL"
              >
                <span className="material-icons text-[14px]">
                  {copied ? "check" : "content_copy"}
                </span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs font-medium rounded-sm text-muted-foreground hover:text-foreground"
                onClick={handleEdit}
              >
                Edit
              </Button>
            </div>
          </div>
          {onRemoveLink && !rich && (
            <>
              <Separator className="my-0.5" />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-full justify-start text-xs text-muted-foreground hover:text-foreground hover:bg-muted px-2"
                onClick={handleRemove}
              >
                <span className="material-icons text-[14px] mr-2">delete</span>
                Remove link
              </Button>
            </>
          )}
        </HoverCardContent>
      )}
    </HoverCard>
  );
}

function tryGetDomain(url: string) {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}
